import React, { useState } from "react";
import {
  Sparkles,
  TrendingUp,
  AlertCircle,
  Lightbulb,
  Target,
  BrainCircuit,
  CheckCircle2,
  ArrowRight,
} from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { aiService } from "../../lib/aiService";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import { ScrollArea } from "../ui/scroll-area";
import { Separator } from "../ui/separator";

export default function AIAnalysisSummary({ appraisal }) {
  const [isOpen, setIsOpen] = useState(false);

  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  React.useEffect(() => {
    if (isOpen && !analysis && !loading) {
      setLoading(true);
      aiService.analyzeData(appraisal)
        .then(data => {
            setAnalysis(data);
            setLoading(false);
        })
        .catch(err => {
            console.error(err);
            setError(err);
            setLoading(false);
        });
    }
  }, [isOpen, appraisal]);

  if (!analysis && !loading) {
      // Allow rendering the button, but maybe wait for dialog content? 
      // Actually the button is outside the dialog content usually?
      // Wait, DialogTrigger children are rendered. DialogContent is what needs data.
  }

  // Fallback for initial render or loading
  const styles = {
    excellent: {
      button: "border-violet-200 hover:border-violet-300 bg-violet-50/50 hover:bg-violet-50 text-violet-700",
      icon: "text-violet-600",
      headerGradient: "from-violet-600 to-violet-800",
      headerText: "text-violet-100",
      titleIcon: "text-violet-600",
      summaryBox: "bg-violet-50 border-violet-100 text-violet-900",
      checkIcon: "text-violet-500",
      numberCircle: "bg-violet-100 text-violet-700",
    },
    good: {
      button: "border-blue-200 hover:border-blue-300 bg-blue-50/50 hover:bg-blue-50 text-blue-700",
      icon: "text-blue-600",
      headerGradient: "from-blue-600 to-blue-800",
      headerText: "text-blue-100",
      titleIcon: "text-blue-600",
      summaryBox: "bg-blue-50 border-blue-100 text-blue-900",
      checkIcon: "text-blue-500",
      numberCircle: "bg-blue-100 text-blue-700",
    },
    needs_improvement: {
      button: "border-amber-200 hover:border-amber-300 bg-amber-50/50 hover:bg-amber-50 text-amber-700",
      icon: "text-amber-600",
      headerGradient: "from-amber-600 to-amber-800",
      headerText: "text-amber-100",
      titleIcon: "text-amber-600",
      summaryBox: "bg-amber-50 border-amber-100 text-amber-900",
      checkIcon: "text-amber-500",
      numberCircle: "bg-amber-100 text-amber-700",
    },
    neutral: {
      button: "border-slate-200 hover:border-slate-300 bg-slate-50/50 hover:bg-slate-50 text-slate-700",
      icon: "text-slate-600",
      headerGradient: "from-slate-600 to-slate-800",
      headerText: "text-slate-100",
      titleIcon: "text-slate-600",
      summaryBox: "bg-slate-50 border-slate-100 text-slate-900",
      checkIcon: "text-slate-500",
      numberCircle: "bg-slate-100 text-slate-700",
    },
  };

  const currentSentiment = analysis?.sentiment || 'neutral';
  const themeStyles = styles[currentSentiment] || styles.neutral;




  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <div className="flex justify-end w-full mt-3">
        <DialogTrigger asChild>
          <Button
            variant="outline"
            className={`
              group relative overflow-hidden 
              transition-all duration-300 shadow-sm hover:shadow
              ${themeStyles.button}
            `}
          >
            <Sparkles
              className={`w-4 h-4 mr-2 ${themeStyles.icon} group-hover:animate-pulse`}
            />
            View AI Analysis
            <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-100%] group-hover:animate-shimmer" />
          </Button>
        </DialogTrigger>
      </div>

      <DialogContent className="max-w-2xl max-h-[85vh] p-0 gap-0 overflow-hidden border-0 shadow-2xl">
        {/* Header Section */}
        <div
          className={`bg-gradient-to-r ${themeStyles.headerGradient} p-6 text-white`}
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm">
              <BrainCircuit className="w-6 h-6 text-white" />
            </div>
            <div>
              <DialogTitle className="text-xl font-bold text-white flex items-center gap-2">
                AI Performance Insights
              </DialogTitle>
              <DialogDescription
                className={`${themeStyles.headerText} text-white/80`}
              >
                Automated analysis based on performance data and feedback
              </DialogDescription>
            </div>
          </div>
        </div>

        <ScrollArea className="max-h-[calc(85vh-100px)]">
          {loading ? (
             <div className="p-12 flex flex-col items-center justify-center text-center">
                <div className="w-12 h-12 border-4 border-violet-200 border-t-violet-600 rounded-full animate-spin mb-4"></div>
                <p className="text-violet-800 font-medium">Analyzing Performance Data...</p>
                <p className="text-sm text-gray-500 mt-1">Our AI is generating custom insights for you.</p>
             </div>
          ) : analysis ? (
          <div className="p-6 space-y-8">
            {/* Executive Summary */}
            <section>
              <h3
                className={`text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2`}
              >
                <Target className={`w-5 h-5 ${themeStyles.titleIcon}`} />
                {analysis.title}
              </h3>
              <div
                className={`p-4 rounded-xl border leading-relaxed ${themeStyles.summaryBox}`}
              >
                {analysis.summary}
              </div>
            </section>

            {/* Key Insights Grid */}
            <section>
              <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
                Key Insights
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {analysis.keyInsights?.map((insight, i) => (
                  <div
                    key={i}
                    className="bg-white border border-gray-100 p-3 rounded-lg shadow-sm flex items-start gap-2"
                  >
                    <CheckCircle2
                      className={`w-4 h-4 mt-0.5 shrink-0 ${themeStyles.checkIcon}`}
                    />
                    <span className="text-sm text-gray-700">{insight}</span>
                  </div>
                ))}
              </div>
            </section>

            <Separator />

            {/* Strengths & Growth Areas */}
            <div className="grid md:grid-cols-2 gap-6">
              <section>
                <h4 className="flex items-center gap-2 font-semibold text-emerald-700 mb-4">
                  <TrendingUp className="w-5 h-5" />
                  Key Strengths
                </h4>
                <div className="space-y-3">
                  {analysis.strengths?.map((item, i) => (
                    <div
                      key={i}
                      className="group p-3 rounded-lg border border-emerald-100 bg-emerald-50/50 hover:bg-emerald-50 transition-colors"
                    >
                      <p className="font-medium text-emerald-900 text-sm">
                        {item.name}
                      </p>
                      <p className="text-xs text-emerald-700 mt-1">
                        {item.desc}
                      </p>
                    </div>
                  ))}
                </div>
              </section>

              <section>
                <h4 className="flex items-center gap-2 font-semibold text-amber-700 mb-4">
                  <AlertCircle className="w-5 h-5" />
                  Areas for Growth
                </h4>
                <div className="space-y-3">
                  {analysis.areasForGrowth?.map((item, i) => (
                    <div
                      key={i}
                      className="group p-3 rounded-lg border border-amber-100 bg-amber-50/50 hover:bg-amber-50 transition-colors"
                    >
                      <p className="font-medium text-amber-900 text-sm">
                        {item.name}
                      </p>
                      <p className="text-xs text-amber-700 mt-1">{item.desc}</p>
                    </div>
                  ))}
                </div>
              </section>
            </div>

            <Separator />

            {/* Recommended Action Plan */}
            <section>
              <h4
                className={`flex items-center gap-2 font-semibold text-gray-700 mb-4`}
              >
                <Lightbulb className="w-5 h-5" />
                Recommended Action Plan
              </h4>
              <div className="space-y-2">
                {analysis.actionPlan?.map((action, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-100"
                  >
                    <div
                      className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${themeStyles.numberCircle}`}
                    >
                      {i + 1}
                    </div>
                    <span className="text-sm text-gray-700">{action}</span>
                    <ArrowRight className="w-4 h-4 text-gray-300 ml-auto" />
                  </div>
                ))}
              </div>
            </section>
          </div>
          ) : (
            <div className="p-8 text-center text-gray-500">
                <p>Unable to generate analysis at this time.</p>
                {error && <p className="text-red-500 text-sm mt-2">{error.message}</p>}
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
